import React, { useState } from 'react'
import type { ServiceItem, ServiceItemGrouped } from '../../utils/serviceItems'
import { CreateServiceItemModal } from './CreateServiceItemModal'

const ADD_NEW = '__add_new__'

export type FlatServiceItemOption = {
  id: string
  name: string
  base_price?: number
  unit_type?: string
  categories?: { name?: string } | null
}

type Props = {
  value: string
  onChange: (
    serviceItemId: string,
    meta?: { name: string; base_price: number; unit_type?: string }
  ) => void
  /** Prefer grouped catalog when available */
  grouped?: ServiceItemGrouped[]
  /** Flat list (e.g. ContractCreate recurring modal) */
  flatItems?: FlatServiceItemOption[]
  /** Called after a new catalog item is created, before onChange — refresh parent lists here */
  onCatalogRefresh?: () => void | Promise<void>
  className?: string
  disabled?: boolean
  emptyOptionLabel?: string
  /** Show an empty/none option (value "") */
  allowEmpty?: boolean
  emptyValueLabel?: string
  /** Show price/unit in grouped options */
  showPrice?: boolean
}

export function ServiceItemSelect({
  value,
  onChange,
  grouped,
  flatItems,
  onCatalogRefresh,
  className = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
  disabled,
  emptyOptionLabel = 'Select service item...',
  allowEmpty = false,
  emptyValueLabel = 'None - Custom Entry',
  showPrice = true,
}: Props) {
  const [showCreate, setShowCreate] = useState(false)

  const resolveMeta = (id: string) => {
    if (!id) return undefined
    const fromGrouped = grouped?.flatMap((g) => g.items).find((i) => i.id === id)
    if (fromGrouped) {
      return {
        name: fromGrouped.name,
        base_price: Number(fromGrouped.base_price) || 0,
        unit_type: fromGrouped.unit_type,
      }
    }
    const fromFlat = flatItems?.find((i) => i.id === id)
    if (fromFlat) {
      return {
        name: fromFlat.name,
        base_price: Number(fromFlat.base_price) || 0,
        unit_type: fromFlat.unit_type,
      }
    }
    return undefined
  }

  const handleSelect = (next: string) => {
    if (next === ADD_NEW) {
      setShowCreate(true)
      return
    }
    onChange(next, resolveMeta(next))
  }

  const handleCreated = async (item: ServiceItem) => {
    try {
      await onCatalogRefresh?.()
    } catch (err) {
      console.error('Catalog refresh after create failed:', err)
    }
    onChange(item.id, {
      name: item.name,
      base_price: item.base_price,
      unit_type: item.unit_type,
    })
  }

  return (
    <>
      <select
        value={value}
        onChange={(e) => handleSelect(e.target.value)}
        className={className}
        disabled={disabled}
      >
        {allowEmpty ? (
          <option value="">{emptyValueLabel}</option>
        ) : (
          <option value="">{emptyOptionLabel}</option>
        )}
        {grouped && grouped.length > 0
          ? grouped.map((group) => (
              <optgroup key={group.category.id} label={group.category.name}>
                {group.items.map((serviceItem) => (
                  <option key={serviceItem.id} value={serviceItem.id}>
                    {showPrice
                      ? `${serviceItem.name} - $${Number(serviceItem.base_price || 0).toFixed(2)}/${(serviceItem.unit_type || 'flat_rate').replace('_', ' ')}`
                      : serviceItem.name}
                  </option>
                ))}
              </optgroup>
            ))
          : null}
        {!grouped?.length &&
          flatItems?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
              {item.categories?.name ? ` (${item.categories.name})` : ''}
            </option>
          ))}
        <option value={ADD_NEW}>+ Add new item…</option>
      </select>
      <CreateServiceItemModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />
    </>
  )
}
